import React, { useCallback, useEffect, useState } from 'react';
import './RequestQuoteModal.css';
import { QuoteRequestApi, ServiceApi } from '../api/adminApi';
import Toast from './Toast';

const RequestQuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    appType: '',
    projectDetails: ''
  });

  const [contactMethod, setContactMethod] = useState('email');
  const [submitState, setSubmitState] = useState({ state: 'idle', message: '' });
  const [toast, setToast] = useState(null);
  const [services, setServices] = useState([]);
  const [appTypeOptions, setAppTypeOptions] = useState([]);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const remote = await ServiceApi.getAll();
        if (Array.isArray(remote)) {
          setServices(remote);
        }
      } catch (err) {
        // fail silently; keep empty options
      }
    };
    loadServices();
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      appType: '',
      projectDetails: ''
    });
    setAppTypeOptions([]);
    setContactMethod('email');
    setSubmitState({ state: 'idle', message: '' });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (e) => {
    const selected = e.target.value;
    setFormData((prev) => ({ ...prev, service: selected, appType: '' }));
    const matched = services.find((svc) => svc.alias === selected || svc.name === selected);
    const mobileTitles = matched?.mobileApps?.map((m) => m.title).filter(Boolean) || [];
    setAppTypeOptions(mobileTitles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitState({ state: 'loading', message: 'Submitting your request…' });
    try {
      await QuoteRequestApi.submit({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        service: formData.service.trim(),
        appType: formData.appType.trim(),
        projectDetails: formData.projectDetails.trim(),
        contactMethod,
        source: 'request-quote-modal'
      });
      setSubmitState({ state: 'success', message: 'Request sent! We will contact you shortly.' });
      resetForm();
      setToast({ type: 'success', message: 'Request sent! We will contact you shortly.' });
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || 'Unable to submit your request right now.';
      setSubmitState({ state: 'error', message });
      setToast({ type: 'error', message });
    }
  };

  return (
    <>
      {isOpen && (
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title">Request a Quote</h2>
            <p className="modal-subtitle">Tell us about your project and we'll get back to you soon</p>
          </div>

          <form className="quote-form" onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Service Required *</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleServiceChange}
                  className="form-control"
                  required
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service.id || service.alias} value={service.alias}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group full-width">
                <label className="form-label">Type of Application (optional)</label>
                <select
                  name="appType"
                  value={formData.appType}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="">Select app type (optional)</option>
                  {[...appTypeOptions, 'Other'].filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i).map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group full-width">
                <label className="form-label">Project Details *</label>
                <textarea
                  name="projectDetails"
                  value={formData.projectDetails}
                  onChange={handleChange}
                  placeholder="Tell us about your project requirements..."
                  className="form-control form-textarea"
                  rows="4"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label className="form-label">Preferred Contact Method</label>
                <div className="contact-methods">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="contactMethod"
                      value="email"
                      checked={contactMethod === 'email'}
                      onChange={(e) => setContactMethod(e.target.value)}
                    />
                    <span className="radio-custom"></span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Email
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="contactMethod"
                      value="phone"
                      checked={contactMethod === 'phone'}
                      onChange={(e) => setContactMethod(e.target.value)}
                    />
                    <span className="radio-custom"></span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M22 16.92V19.92C22.0011 20.4831 21.7441 21.0211 21.2951 21.3962C20.8462 21.7714 20.2478 21.948 19.6562 21.8852C16.7346 21.5347 13.9387 20.532 11.4862 18.96C9.21064 17.5401 7.27234 15.6018 5.85246 13.3262C4.27752 10.866 3.27553 8.06094 2.92746 5.13124C2.86476 4.54131 3.04075 3.94482 3.41466 3.49697C3.78858 3.04912 4.32506 2.79137 4.88746 2.79124H7.88746C8.93556 2.78097 9.82707 3.53839 9.97746 4.57124C10.1121 5.55284 10.3594 6.51611 10.7137 7.44124C11.0019 8.19056 10.8174 9.03603 10.2387 9.61874L8.98746 10.8687C10.3035 13.2458 12.341 15.2833 14.7187 16.5987L15.9687 15.3487C16.5514 14.77 17.3969 14.5855 18.1462 14.8737C19.0714 15.228 20.0346 15.4754 21.0162 15.61C22.0591 15.7617 22.8229 16.6674 22.8087 17.7287L22 16.92Z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Phone
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="contactMethod"
                      value="whatsapp"
                      checked={contactMethod === 'whatsapp'}
                      onChange={(e) => setContactMethod(e.target.value)}
                    />
                    <span className="radio-custom"></span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitState.state === 'loading'}>
                {submitState.state === 'loading' ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
        {submitState.state === 'loading' && (
          <div className="modal-loader" role="status" aria-live="polite">
            <div className="modal-spinner" />
            <span>Submitting your request…</span>
          </div>
        )}
          </div>
        </div>
      )}
      <Toast
        message={toast?.message}
        type={toast?.type}
        onDismiss={() => setToast(null)}
        position="bottom-right"
      />
    </>
  );
};

export default RequestQuoteModal;
